import { useCallback, useRef, useState } from "react";
import { Upload, X, Image, Video } from "lucide-react";
import { Button } from "@mui/material";
import { urlToFile } from "../../../utils/utility";

const DarkFileUploader = ({ onFilesUpload, uploadedFiles, selectedAPI }) => {
  const fileInput = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setIsDragOver(false);
      const data = e.dataTransfer.getData("text/plain");
      const dataObject = data ? JSON.parse(data) : {};

      const files = Array.from(e.dataTransfer.files);
      if (dataObject && dataObject.mediaFileUrl) {
        const file = await urlToFile(
          dataObject.mediaFileUrl,
          `media-${dataObject.id}`,
          dataObject.mimeType
        );
        files.push(file);
      }
      processFiles(files);
    },
    [uploadedFiles, onFilesUpload]
  );

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        processFiles(files);
      }
    },
    [uploadedFiles, onFilesUpload]
  );

  const processFiles = (files) => {
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    const newUploadedFiles = validFiles.map((file) => {
      const id = Math.random().toString(36).substr(2, 9);
      const preview = URL.createObjectURL(file);
      const type = file.type.startsWith("image/") ? "image" : "video";
      return { id, file, preview, type };
    });

    onFilesUpload([...uploadedFiles, ...newUploadedFiles]);
  };

  const removeFile = (id) => {
    const updatedFiles = uploadedFiles.filter((file) => file.id !== id);
    onFilesUpload(updatedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left: Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 min-h-[200px] flex flex-col justify-center
          ${
            isDragOver
              ? "border-teal-400 bg-teal-900/20"
              : "border-gray-600 hover:border-teal-500 hover:bg-gray-700/50"
          }
        `}
      >
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-gray-300 mb-2">
          Drop files here
        </h3>
        <p className="text-xs text-gray-500 mb-3">Images & Videos</p>
        <label className="cursor-pointer">
          <Button
            variant="outline"
            size="sm"
            className="bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500"
            onClick={(e) => {
              e.preventDefault();
              fileInput.current.click();
            }}
          >
            Browse Files
          </Button>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileInput}
            className="hidden"
            ref={fileInput}
          />
        </label>
      </div>

      {/* Right: Uploaded Files */}
      <div className="min-h-[200px]">
        {uploadedFiles.length > 0 ? (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-300 text-sm">
              Uploaded Files ({uploadedFiles.length})
            </h4>
            <div className="grid grid-cols-4 gap-2 max-h-[160px] overflow-y-auto">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="relative group">
                  <div className="aspect-9/16 rounded overflow-hidden bg-gray-700 border border-gray-600">
                    {file.type === "image" ? (
                      <img
                        src={file.preview}
                        alt={file.file.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error(
                            "Image failed to load:",
                            file.file.name
                          );
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        <video
                          src={file.preview}
                          alt={file.file.name}
                          className="w-full h-full object-contain"
                          controls
                          onError={(e) => {
                            console.error(
                              "Image failed to load:",
                              file.file.name
                            );
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}

                    {/* File type indicator */}
                    <div className="absolute top-1 left-1">
                      <div className="bg-black bg-opacity-60 rounded px-1 py-0.5 flex items-center space-x-1">
                        {file.type === "image" ? (
                          <Image className="w-2 h-2 text-white" />
                        ) : (
                          <Video className="w-2 h-2 text-white" />
                        )}
                        <span className="text-xs text-white uppercase">
                          {file.type}
                        </span>
                      </div>
                    </div>

                    {/* Remove button */}
                    <button
                      onClick={() => removeFile(file.id)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2 h-2" />
                    </button>
                  </div>

                  {/* File name */}
                  <p
                    className="text-xs text-gray-500 mt-1 truncate"
                    title={file.file.name}
                  >
                    {file.file.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
                <Image className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-xs text-gray-500">No files uploaded</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default DarkFileUploader;
